import React, { useState, FC } from "react";
import FadeIn from "react-fade-in";
import {
  FiZap,
  FiFileText,
  FiSearch,
  FiX,
} from "react-icons/fi";
import { BiFileBlank } from "react-icons/bi";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ScatterController,
} from "chart.js";
import { Line, Bar, Scatter, Chart } from "react-chartjs-2";
import Modal from "../../../atom/modal";
import Table from "../../../atom/table";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ScatterController,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface TranscriptionAnalyticsProps {
  startDate: string;
  endDate: string;
  refreshTrigger?: number;
}

const TranscriptionAnalytics: FC<TranscriptionAnalyticsProps> = ({
  startDate,
  endDate,
  refreshTrigger,
}) => {
  const [isRawRecordsModalOpen, setIsRawRecordsModalOpen] = useState(false);
  const [isHourlySummaryModalOpen, setIsHourlySummaryModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [summarySearchQuery, setSummarySearchQuery] = useState("");

  // Mock Raw Records data
  const rawRecordsData = [
    { id: "62ee39b2-bcdd-4e21-bf95-de3a4164f48c", created_at: "2026-05-29T07:22:13.923000", audio_duration: "6.9s", queue_wait: "0s", process_time: "0s", status: "COMPLETED" },
    { id: "333195d7-943e-4345-998f-dd7bb7ae3e45", created_at: "2026-05-29T07:21:37.583000", audio_duration: "52.2s", queue_wait: "0s", process_time: "2s", status: "COMPLETED" },
    { id: "2571589d-fc58-405a-ab9a-6462045495bd", created_at: "2026-05-29T07:14:59.878000", audio_duration: "11.4s", queue_wait: "0s", process_time: "2s", status: "COMPLETED" },
    { id: "6d4fc224-47c8-4a81-a2f7-b2d955341b6b", created_at: "2026-05-29T07:10:37.268000", audio_duration: "46.0s", queue_wait: "0s", process_time: "1s", status: "COMPLETED" },
    { id: "b5c88d11-fd88-41b5-ab23-e12998b7df52", created_at: "2026-05-29T07:09:00.490000", audio_duration: "93.1s", queue_wait: "0s", process_time: "2s", status: "COMPLETED" },
    { id: "8b98102c-0023-4edc-ad44-7ad4a02e95e9", created_at: "2026-05-29T07:02:59.870000", audio_duration: "7.3s", queue_wait: "0s", process_time: "0s", status: "COMPLETED" },
    { id: "6e7cac11-4774-4581-999c-e4a487d5acb4", created_at: "2026-05-29T07:00:59.853000", audio_duration: "8.9s", queue_wait: "0s", process_time: "0s", status: "COMPLETED" },
    { id: "5fb1cbc6-d9c7-4b1f-943e-f85587603bcd", created_at: "2026-05-29T06:56:00.405000", audio_duration: "92.2s", queue_wait: "0s", process_time: "2s", status: "COMPLETED" },
    { id: "e5d14a3c-3051-4c8b-8e93-ab98d89ee76e", created_at: "2026-05-29T06:31:14.048000", audio_duration: "21.3s", queue_wait: "0s", process_time: "1s", status: "COMPLETED" },
    { id: "fd90bcc1-1aec-48d1-bcd7-af6205e8aca7", created_at: "2026-05-29T06:11:13.910000", audio_duration: "7.0s", queue_wait: "0s", process_time: "0s", status: "COMPLETED" },
    { id: "6a1b42bc-6d56-49f6-ba34-8633902f81f6", created_at: "2026-05-29T06:19:39.180000", audio_duration: "282.7s", queue_wait: "0s", process_time: "0s", status: "COMPLETED" },
    { id: "8e12b61e-c86e-49b1-b4d5-fe279633de78", created_at: "2026-05-29T06:49:01.165000", audio_duration: "164.7s", queue_wait: "0s", process_time: "3s", status: "COMPLETED" },
    { id: "98ae8f83-cc9d-4b7b-8e4c-a2b8f8dda2bc", created_at: "2026-05-29T06:45:14.259000", audio_duration: "52.7s", queue_wait: "0s", process_time: "1s", status: "COMPLETED" },
  ];

  // Mock Hourly Summary data
  const hourlySummaryData = [
    { hour: "05:00", req_volume: 142, processed: 142, audio_in: "328.3", audio_out: "328.3", avg_queue: "0.01s", avg_process: "2.15s" },
    { hour: "06:00", req_volume: 162, processed: 151, audio_in: "187.8", audio_out: "187.1", avg_queue: "0.00s", avg_process: "1.23s" },
    { hour: "07:00", req_volume: 93, processed: 93, audio_in: "183.8", audio_out: "184.2", avg_queue: "0.00s", avg_process: "1.93s" },
    { hour: "08:00", req_volume: 48, processed: 49, audio_in: "52.9", audio_out: "53.2", avg_queue: "0.00s", avg_process: "1.14s" },
    { hour: "09:00", req_volume: 27, processed: 27, audio_in: "30.6", audio_out: "30.6", avg_queue: "0.00s", avg_process: "1.41s" },
    { hour: "10:00", req_volume: 30, processed: 30, audio_in: "40.0", audio_out: "40.0", avg_queue: "0.00s", avg_process: "1.53s" },
    { hour: "11:00", req_volume: 20, processed: 20, audio_in: "28.7", audio_out: "28.7", avg_queue: "0.00s", avg_process: "1.55s" },
    { hour: "12:00", req_volume: 17, processed: 17, audio_in: "17.6", audio_out: "17.6", avg_queue: "0.00s", avg_process: "1.29s" },
  ];

  const tableTitles = [
    { title: "Job Id", key: "id", class: "w-[30%] font-medium text-gray-700" },
    { title: "Created At", key: "created_at", class: "w-[25%] font-medium text-gray-600" },
    { title: "Audio Duration", key: "audio_duration", class: "w-[15%] font-medium text-gray-600" },
    { title: "Queue Wait", key: "queue_wait", class: "w-[10%] font-medium text-gray-600" },
    { title: "Process Time", key: "process_time", class: "w-[10%] font-medium text-gray-600" },
    { title: "Status", key: "status", class: "w-[10%]" },
  ];

  const summaryTitles = [
    { title: "Hour", key: "hour", class: "w-[20%] font-bold text-gray-700" },
    { title: "Req Volume", key: "req_volume", class: "w-[20%] font-medium text-gray-600" },
    { title: "Processed", key: "processed", class: "w-[20%] font-medium text-gray-600" },
    { title: "Avg Queue", key: "avg_queue", class: "w-[20%] font-medium text-gray-600" },
    { title: "Avg Process", key: "avg_process", class: "w-[20%] font-medium text-gray-600" },
  ];

  const formattedTableData = rawRecordsData
    .filter(row => row.id.toLowerCase().includes(searchQuery.toLowerCase()))
    .map(row => ({
      ...row,
      id: row.id,
      status: (
        <span className="bg-green-50 text-green-600 text-[10px] font-bold px-2 py-1 rounded-md">
          {row.status}
        </span>
      ),
    }));

  const formattedSummaryData = hourlySummaryData
    .filter(row => row.hour.toLowerCase().includes(summarySearchQuery.toLowerCase()))
    .map(row => ({
        ...row,
        hour: <span className="font-bold text-gray-800">{row.hour}</span>
    }));

  const labels = ["05:00", "06:00", "07:00", "08:00", "09:00", "10:00", "11:00", "12:00", "13:00", "14:00", "15:00", "16:00"];

  // 1. Load vs. Analytics Performance (Hourly)
  const loadAnalyticsData = {
    labels,
    datasets: [
      {
        type: "bar" as const,
        label: "Incoming Requests",
        data: [40, 58, 48, 32, 20, 24, 12, 18, 18, 15, 12, 3],
        backgroundColor: "rgba(219, 39, 119, 0.2)",
        borderRadius: 4,
        barThickness: 30,
      } as any,
      {
        type: "line" as const,
        label: "Processed Analytics Jobs",
        data: [40, 58, 49, 33, 21, 24, 13, 18, 18, 15, 12, 3],
        borderColor: "#db2777",
        borderWidth: 2,
        pointBackgroundColor: "#db2777",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        tension: 0.4,
      } as any,
    ],
  };

  // 2. Workload Volume vs. Analytics Queue Wait
  const workloadQueueData = {
    labels,
    datasets: [
      {
        type: "bar" as const,
        label: "Incoming Workload",
        data: [40, 58, 48, 32, 21, 24, 13, 18, 18, 15, 12, 3],
        backgroundColor: "rgba(219, 39, 119, 0.1)",
        borderRadius: 4,
        barThickness: 30,
      } as any,
      {
        type: "line" as const,
        label: "Avg Queue Wait",
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        borderColor: "#db2777",
        borderWidth: 2,
        pointBackgroundColor: "#db2777",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        tension: 0.4,
      } as any,
    ],
  };

  // 3. Workload Volume vs. Analytics Effort (Hourly)
  const workloadEffortData = {
    labels,
    datasets: [
      {
        type: "bar" as const,
        label: "Audio Arrived (min)",
        data: [55, 85, 52, 35, 32, 55, 25, 35, 45, 18, 15, 5],
        backgroundColor: "rgba(249, 115, 22, 0.1)",
        borderRadius: 4,
        barThickness: 30,
      } as any,
      {
        type: "line" as const,
        label: "Audio Analyzed (min)",
        data: [58, 88, 54, 36, 34, 75, 26, 36, 47, 18, 16, 6],
        borderColor: "#f97316",
        borderWidth: 2,
        pointBackgroundColor: "#f97316",
        pointBorderColor: "#fff",
        pointBorderWidth: 2,
        tension: 0.4,
      } as any,
      {
        type: "line" as const,
        label: "Analytics Machine Effort (min)",
        data: [70, 95, 75, 45, 38, 56, 28, 40, 42, 22, 20, 8],
        borderColor: "#f97316",
        borderDash: [5, 5],
        borderWidth: 1.5,
        pointRadius: 0,
        tension: 0.4,
      } as any,
    ],
  };

  // 4. Avg processing Time Comparison
  const processingTimeData = {
    labels: ["Webhook", "Analytics", "STT"],
    datasets: [
      {
        label: "Time (s)",
        data: [0.01, 3.8, 1.8],
        backgroundColor: "rgba(30, 64, 175, 0.4)",
        borderRadius: 4,
        barThickness: 25,
      },
    ],
  };

  // 5. Duration vs Analytics processing Time
  const scatterData = {
    datasets: [
      {
        label: "Duration vs Analytics Time",
        data: Array.from({ length: 60 }, () => ({
          x: Math.random() * 600,
          y: Math.random() * 10,
        })).concat([
            { x: 2300, y: 12 }
        ]),
        backgroundColor: "#db2777",
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: { size: 12 },
        },
      },
      tooltip: {
        mode: "index" as const,
        intersect: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          borderDash: [5, 5],
          color: "rgba(0, 0, 0, 0.05)",
        },
      },
      x: {
        grid: {
          display: false,
        },
      },
    },
  };

  const horizontalBarOptions = {
    ...chartOptions,
    indexAxis: 'y' as const,
    scales: {
        ...chartOptions.scales,
        x: {
            beginAtZero: true,
            title: { display: true, text: 's', position: 'end' as const }
        }
    }
  };

  return (
    <div className="flex flex-col gap-6 w-full">
      {isRawRecordsModalOpen && (
        <Modal
          closeEvent={() => setIsRawRecordsModalOpen(false)}
          width="w-full lg:max-w-[1200px]"
          className="rounded-3xl"
          bodyClassName="p-3 sm:p-6"
          hideClose={true}
        >
          <div className="flex flex-col gap-4 sm:gap-6 w-full overflow-hidden">
            {/* Modal Header */}
            <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 line-clamp-1">Analytics Raw Metrics Data</h2>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">Showing all processed records for the selected period</p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
                 <div className="relative flex-1 sm:flex-initial">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input 
                        type="text" 
                        placeholder="Search records..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 sm:min-w-[240px]"
                    />
                 </div>
                 <div className="flex items-center justify-between sm:justify-start gap-2 bg-gray-50 p-1 rounded-xl border border-gray-100">
                    <button className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-white hover:shadow-sm rounded-lg transition-all text-blue-500">
                        <BiFileBlank size={14} /> CSV
                    </button>
                    <button className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-white hover:shadow-sm rounded-lg transition-all text-blue-500">
                        <BiFileBlank size={14} /> JSON
                    </button>
                 </div>
                 <button 
                    onClick={() => setIsRawRecordsModalOpen(false)}
                    className="hidden lg:flex p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
                 >
                    <FiX size={20} />
                 </button>
              </div>
            </div>

            {/* Modal Table container */}
            <div className="w-full border border-gray-100 rounded-2xl shadow-sm overflow-x-auto min-w-0">
                <div className="min-w-[800px] lg:min-w-0">
                    <Table 
                        tableTitles={tableTitles}
                        tableData={formattedTableData}
                        width="w-full"
                        scrollHeight="max-h-[50vh] sm:max-h-[500px]"
                        rowColorClass="bg-white"
                    />
                </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-center text-center px-4">
                <p className="text-[10px] sm:text-xs text-gray-400 font-medium">
                    Showing {rawRecordsData.length} records • Total data size: 352.6 KB
                </p>
            </div>
          </div>
        </Modal>
      )}

      {isHourlySummaryModalOpen && (
        <Modal
          closeEvent={() => setIsHourlySummaryModalOpen(false)}
          width="w-full lg:max-w-[1200px]"
          className="rounded-3xl"
          bodyClassName="p-3 sm:p-6"
          hideClose={true}
        >
          <div className="flex flex-col gap-4 sm:gap-6 w-full overflow-hidden">
            {/* Modal Header */}
            <div className="flex flex-col lg:flex-row justify-between items-start gap-4">
              <div>
                <h2 className="text-xl sm:text-2xl font-bold text-gray-800 line-clamp-1">Analytics Hourly Summary Data</h2>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">Showing all processed records for the selected period</p>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
                 <div className="relative flex-1 sm:flex-initial">
                    <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input 
                        type="text" 
                        placeholder="Search records..." 
                        value={summarySearchQuery}
                        onChange={(e) => setSummarySearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-100 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-100 sm:min-w-[240px]"
                    />
                 </div>
                 <div className="flex items-center justify-between sm:justify-start gap-2 bg-gray-50 p-1 rounded-xl border border-gray-100">
                    <button className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-white hover:shadow-sm rounded-lg transition-all text-blue-500">
                        <BiFileBlank size={14} /> CSV
                    </button>
                    <button className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-white hover:shadow-sm rounded-lg transition-all text-blue-500">
                        <BiFileBlank size={14} /> JSON
                    </button>
                 </div>
                 <button 
                    onClick={() => setIsHourlySummaryModalOpen(false)}
                    className="hidden lg:flex p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400"
                 >
                    <FiX size={20} />
                 </button>
              </div>
            </div>

            {/* Modal Table container */}
            <div className="w-full border border-gray-100 rounded-2xl shadow-sm overflow-x-auto min-w-0">
                <div className="min-w-[800px] lg:min-w-0">
                    <Table 
                        tableTitles={summaryTitles}
                        tableData={formattedSummaryData}
                        width="w-full"
                        scrollHeight="max-h-[50vh] sm:max-h-[500px]"
                        rowColorClass="bg-white"
                    />
                </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-center text-center px-4">
                <p className="text-[10px] sm:text-xs text-gray-400 font-medium">
                    Showing {hourlySummaryData.length} records • Total data size: 7.7 KB
                </p>
            </div>
          </div>
        </Modal>
      )}

      {/* Hourly Summary Toggle */}
      <div className="flex justify-end gap-2 mb-2">
        <div className="flex bg-white rounded-xl p-1 shadow-sm border border-[#ccc]">
          <button 
            onClick={() => setIsHourlySummaryModalOpen(true)}
            className={`px-4 py-2 text-sm font-semibold rounded-lg flex items-center gap-2 transition-all duration-200 ${
              isHourlySummaryModalOpen 
                ? "bg-[#feba12] text-white shadow-md" 
                : "text-gray-500 hover:bg-[#feba12] hover:text-white"
            }`}
          >
             <FiZap size={14} /> Hourly Summary
          </button>
          <button 
            onClick={() => setIsRawRecordsModalOpen(true)}
            className={`px-4 py-2 text-sm font-semibold rounded-lg flex items-center gap-2 transition-all duration-200 ${
              isRawRecordsModalOpen 
                ? "bg-[#feba12] text-white shadow-md" 
                : "text-gray-500 hover:bg-[#feba12] hover:text-white"
            }`}
          >
             <FiFileText size={14} /> Raw Records
          </button>
        </div>
      </div>

      {/* Graphs Section */}
      <div className="grid grid-cols-1 gap-6">
        {/* Row 1 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-base font-bold text-gray-800 mb-6">Load vs. Analytics Performance (Hourly)</h3>
          <div className="h-[300px]">
            <Chart type="bar" data={loadAnalyticsData as any} options={chartOptions as any} />
          </div>
        </div>

        {/* Row 2 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-base font-bold text-gray-800 mb-6">Workload Volume vs. Analytics Queue Wait</h3>
          <div className="h-[300px]">
            <Chart type="bar" data={workloadQueueData as any} options={chartOptions as any} />
          </div>
        </div>

        {/* Row 3 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-base font-bold text-gray-800 mb-6">Workload Volume vs. Analytics Effort (Hourly)</h3>
          <div className="h-[300px]">
            <Chart type="bar" data={workloadEffortData as any} options={chartOptions as any} />
          </div>
        </div>

        {/* Row 4 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-base font-bold text-gray-800 mb-6">Avg processing Time Comparison</h3>
          <div className="h-[300px]">
            <Bar data={processingTimeData} options={horizontalBarOptions as any} />
          </div>
        </div>

        {/* Row 5 */}
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
          <h3 className="text-base font-bold text-gray-800 mb-6">Duration vs Analytics processing Time</h3>
          <div className="h-[300px]">
            <Scatter 
                data={scatterData} 
                options={{
                    ...chartOptions,
                    scales: {
                        ...chartOptions.scales,
                        x: {
                            type: 'linear',
                            position: 'bottom',
                            title: { display: true, text: 'Audio Duration (s)', font: { size: 10 } },
                            grid: { borderDash: [5, 5], color: 'rgba(0, 0, 0, 0.05)' }
                        },
                        y: {
                            title: { display: true, text: 'Analytics Time (s)', font: { size: 10 } },
                            grid: { borderDash: [5, 5], color: 'rgba(0, 0, 0, 0.05)' }
                        }
                    }
                } as any} 
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TranscriptionAnalytics;
